import { activeSet, DomainError, liveItems, normalizeWord, type Account, type Item, type ItemInput, type StudySet } from './model';
import { addDays, taipeiDate } from './schedule';

export function mergeChinese(previous: string, incoming: string): string {
  return [...new Set([...previous.split(/[；;、]/), ...incoming.split(/[；;、]/)].map(value => value.trim()).filter(Boolean))].join('；');
}
export function findItem(account: Account, itemId: string): { set: StudySet; item: Item } {
  for (const set of account.sets) {
    if (!activeSet(account, set)) continue;
    const item = liveItems(set).find(candidate => candidate.itemId === itemId);
    if (item) return { set, item };
  }
  throw new DomainError('conflict', '此項目已刪除或已移出複習');
}
export function newSet(account: Account, kind: StudySet['kind'], categoryId: string, title: string, now: number, uuid: () => string): StudySet {
  const createdDate = taipeiDate(now);
  return { setId: uuid(), kind, categoryId, title, wordEpoch: account.wordEpoch, contentRevision: 1,
    createdAt: now, createdDate, noSchedule: false, deleted: false, items: [],
    stage: 0, remaining: 3, totalRounds: 0, dueDate: addDays(createdDate, 1), cooldownUntil: 0, lastAssignedDate: '' };
}

export function addItems(account: Account, input: {
  kind: StudySet['kind']; items: ItemInput[]; targetSetId?: string; contentRevision?: number; categoryId: string; title: string;
}, now: number, uuid: () => string): Record<string, unknown> {
  const target = input.targetSetId ? account.sets.find(set => set.setId === input.targetSetId && activeSet(account, set)) : undefined;
  if (input.targetSetId && (!target || target.kind !== input.kind || target.contentRevision !== input.contentRevision)) {
    throw new DomainError('conflict', '卡組內容已變更，請重新取得');
  }
  const categoryId = target?.categoryId ?? input.categoryId;
  if (categoryId && !account.categories.some(category => category.categoryId === categoryId)) throw new DomainError('invalid', '分類不存在');
  const fresh = newSet(account, input.kind, categoryId, input.title, now, uuid);
  const active = new Map<string, { set: StudySet; item: Item }>();
  const dormant = new Map<string, Item>();
  if (input.kind === 'word') {
    for (const set of account.sets.filter(set => !set.deleted && set.kind === 'word').sort((a, b) =>
      a.wordEpoch - b.wordEpoch || a.createdAt - b.createdAt || a.setId.localeCompare(b.setId))) {
      for (const item of liveItems(set)) {
        if (activeSet(account, set)) active.set(normalizeWord(item.en), { set, item });
        else dormant.set(normalizeWord(item.en), item);
      }
    }
  }
  const changed = new Set<StudySet>();
  const conflicts: { itemId: string; en: string; existingContext: string; incomingContext: string }[] = [];
  let added = 0, reactivated = 0, merged = 0;
  for (const data of input.items) {
    const key = normalizeWord(data.en);
    const existing = active.get(key);
    if (existing) {
      const zh = mergeChinese(existing.item.zh, data.zh);
      if (zh !== existing.item.zh) { existing.item.zh = zh; changed.add(existing.set); }
      if (data.context && data.context !== existing.item.context) {
        if (existing.item.context) conflicts.push({ itemId: existing.item.itemId, en: existing.item.en,
          existingContext: existing.item.context, incomingContext: data.context });
        else { existing.item.context = data.context; changed.add(existing.set); }
      }
      merged++;
      continue;
    }
    const old = dormant.get(key);
    const item: Item = { itemId: uuid(), en: data.en.trim(), zh: mergeChinese(old?.zh ?? '', data.zh),
      context: data.context || old?.context || '', deleted: false, starred: old?.starred ?? false,
      wrongCount: old?.wrongCount ?? 0, correctStreak: 0,
      ...(old ? { reactivatedFromItemId: old.itemId } : {}) };
    const destination = old && target && target.totalRounds > 0 ? fresh : target ?? fresh;
    destination.items.push(item);
    if (destination === target) changed.add(destination);
    if (input.kind === 'word') active.set(key, { set: destination, item });
    added++;
    if (old) reactivated++;
  }
  for (const set of changed) set.contentRevision++;
  if (fresh.items.length) account.sets.push(fresh);
  return { added, merged, reactivated, conflicts, newSetId: fresh.items.length ? fresh.setId : null,
    separateLearningSet: !!(reactivated && target && target.totalRounds > 0) };
}
